import React, { createContext, useContext, useEffect, useState } from "react";

import { makeStyles, Typography } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { Theme } from "@material-ui/core/styles";

import Button from "../../../components/Button";
import { HospitalsContext } from "../RodaHomepage";
import { getHospitalInfoById } from "../../../api/hospital";
import { HospitalInfo } from "../../../types/hospital";
import ModalDialog from "../../../components/Form/ModalDialog";
import HospitalInfoForm from "./HospitalInfoForm";
import { errorCodes } from "../../../constants/errors";
import { HospitalInfoContextType } from "../types";
import LightPurpleContainer from "../../../components/LightPurpleContainer";
import { blankHospitalInfo } from "../../../constants/objects";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    backgroundColor: "#fff",
    borderRadius: 4,
    [theme.breakpoints.only("xs")]: {
      display: "block",
    },
  },
  head: {
    textTransform: "uppercase",
    color: theme.palette.secondary.main,
    fontWeight: "bold",
    padding: theme.spacing(3, 0, 3, 6),
    [theme.breakpoints.only("xs")]: {
      display: "block",
    },
  },
  info: {
    width: "55%",
    fontWeight: "normal",
    [theme.breakpoints.only("xs")]: {
      display: "block",
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      width: "auto",
    },
  },
  editIcon: {
    fontSize: theme.spacing(2.2),
    color: theme.palette.primary.main,
  },
  title: {
    color: theme.palette.primary.main,
    display: "inline-block",
    fontSize: theme.spacing(2.5),
    fontWeight: "bold",
    margin: theme.spacing(1.8),
  },
  marginTop: {
    marginTop: theme.spacing(3),
  },
}));

export const HospitalInfoContext = createContext<HospitalInfoContextType>({
  hospitalInfo: blankHospitalInfo,
  setHospitalInfo: () => {},
});

export default function HospitalInfoTable() {
  const classes = useStyles();
  const [hospital, setHospital] = useState<HospitalInfo>(blankHospitalInfo);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { currentHospital, setError } = useContext(HospitalsContext);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getHospitalInfoById(currentHospital);
        setHospital(response.data);
      } catch (reason: any) {
        if (
          reason.response &&
          reason.response.status === errorCodes.NOT_FOUND
        ) {
          setError(true);
        }
      }
    }

    fetchData();
  }, [currentHospital, setError]);

  return (
    <>
      <HospitalInfoContext.Provider
        value={{ hospitalInfo: hospital, setHospitalInfo: setHospital }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="button"
            startIcon={<CreateIcon />}
            onClick={() => setModalOpen(true)}
          >
            Uredi
          </Button>
          {modalOpen && (
            <ModalDialog
              modalOpen={modalOpen}
              handleClose={() => setModalOpen(false)}
              maxWidth="lg"
              title={<Typography className={classes.title}>Uredi</Typography>}
              icon={<CreateIcon className={classes.editIcon} />}
            >
              <HospitalInfoForm handleClose={() => setModalOpen(false)} />
            </ModalDialog>
          )}
        </div>
      </HospitalInfoContext.Provider>
      <LightPurpleContainer className={classes.marginTop}>
        <Table className={classes.table} aria-label="simple table">
          <TableBody>
            <>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Ime ustanove
                </TableCell>
                <TableCell className={classes.info}>{hospital.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Regija
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.cityRegionName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Adresa
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.streetAddress}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Grad
                </TableCell>
                <TableCell className={classes.info}>
                  {`${hospital.cityName} ${hospital.cityPostcode}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Broj telefona 1.
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.phone1}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Broj telefona 2.
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.phone2 ? hospital.phone2 : "Nema podataka"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Link na web stranicu
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.website ? hospital.website : "Nema podataka"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head" className={classes.head}>
                  Email
                </TableCell>
                <TableCell className={classes.info}>
                  {hospital.email ? hospital.email : "Nema podataka"}
                </TableCell>
              </TableRow>
            </>
          </TableBody>
        </Table>
      </LightPurpleContainer>
    </>
  );
}
